import React from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import Button from '~components/Button';
import Icon, { faTriangleExclamation } from '~components/icon';

import { setOptionRequiresRebuild } from '~weekly-planner/reducers/weeklyPlanner';

import { selectRequiredRebuild } from '~weekly-planner/selectors/simulate';

import { CommonOptions } from './CommonOptions';

interface ComponentProps {
  appointmentFlexibility: boolean;
  showSystemUsers: boolean;
  showBreaks: boolean;
  showTeaBreaks: boolean;
  showTravel: boolean;
  showCancellations: boolean;
  showPayableCancellations: boolean;
  showServiceType: boolean;
  showCardQualifications: boolean;
  changeOptions: (options: Array<{ value: boolean; parameterKey: string; optionKey: string }>) => void;
  refresh: () => void;
  setModalOpen: (open: boolean) => void;
}

export const PlannerOptions: React.FC<ComponentProps> = ({
  appointmentFlexibility,
  showSystemUsers,
  showBreaks,
  showTeaBreaks,
  showTravel,
  showCancellations,
  showPayableCancellations,
  showServiceType,
  showCardQualifications,
  changeOptions,
  refresh,
  setModalOpen,
}) => {
  const dispatch = useAppDispatch();
  const requiresRebuild = useAppSelector(selectRequiredRebuild);

  const options = [
    {
      id: 'flexibility',
      label: 'Flexibility',
      tooltip: 'Show scheduling flexibility indicators on appointments',
      checked: appointmentFlexibility,
      onChange: () =>
        changeOptions([
          {
            value: !appointmentFlexibility,
            parameterKey: 'appointment_flexibility',
            optionKey: 'appointmentFlexibility',
          },
        ]),
    },
    {
      id: 'breaks',
      label: 'Breaks',
      tooltip: 'Show scheduled breaks in the planner',
      checked: showBreaks,
      onChange: () => changeOptions([{ value: !showBreaks, parameterKey: 'show_breaks', optionKey: 'showBreaks' }]),
    },
    {
      id: 'teaBreaks',
      label: 'Tea Breaks',
      tooltip: 'Display short tea/coffee breaks separately',
      checked: showTeaBreaks,
      onChange: () => changeOptions([{ value: !showTeaBreaks, parameterKey: 'tea_break', optionKey: 'showTeaBreaks' }]),
    },
    {
      id: 'travel',
      label: 'Travel',
      tooltip: 'Show travel time between appointments',
      checked: showTravel,
      onChange: () => changeOptions([{ value: !showTravel, parameterKey: 'show_travel', optionKey: 'showTravel' }]),
    },
    {
      id: 'serviceType',
      label: 'Service Types',
      tooltip: 'Display service type labels on appointments',
      checked: showServiceType,
      onChange: () =>
        changeOptions([{ value: !showServiceType, parameterKey: 'show_service_type', optionKey: 'showServiceType' }]),
    },
    {
      id: 'card-qualifications',
      label: 'Appointment Qualifications',
      tooltip: 'Show required qualifications on appointments',
      checked: showCardQualifications,
      onChange: () =>
        changeOptions([
          { value: !showCardQualifications, parameterKey: 'show_qualifications', optionKey: 'showCardQualifications' },
        ]),
    },
    {
      id: 'show-system-users',
      label: 'Display System Users',
      tooltip: 'Show system only users in the worker list',
      checked: showSystemUsers,
      onChange: () =>
        changeOptions([{ value: !showSystemUsers, parameterKey: 'show_system_users', optionKey: 'showSystemUsers' }]),
    },
    {
      id: 'cancellations',
      label: 'Show Cancellations',
      tooltip: 'Configure cancellation display options',
      type: 'radio' as const,
      children: [
        {
          id: 'payableCancellations',
          label: 'Payable Cancellations',
          tooltip: 'Show all payable cancelled appointments in the planner',
          checked: !showCancellations && showPayableCancellations,
          onChange: () => {
            changeOptions([
              { value: false, parameterKey: 'show_cancellations', optionKey: 'showCancellations' },
              { value: true, parameterKey: 'show_payable_cancellations', optionKey: 'showPayableCancellations' },
            ]);
          },
        },
        {
          id: 'allCancellations',
          label: 'All Cancellations',
          tooltip: 'Show all payable & non-payable cancellations in the planner',
          checked: showPayableCancellations && showCancellations,
          onChange: () => {
            changeOptions([
              { value: true, parameterKey: 'show_cancellations', optionKey: 'showCancellations' },
              { value: true, parameterKey: 'show_payable_cancellations', optionKey: 'showPayableCancellations' },
            ]);
          },
        },
        {
          id: 'noCancellations',
          label: 'None',
          tooltip: 'Hide all cancellations',
          checked: !showCancellations && !showPayableCancellations,
          onChange: () => {
            changeOptions([
              { value: false, parameterKey: 'show_cancellations', optionKey: 'showCancellations' },
              { value: false, parameterKey: 'show_payable_cancellations', optionKey: 'showPayableCancellations' },
            ]);
          },
        },
      ],
    },
  ];

  const closeAndRebuild = () => {
    setModalOpen(false);
    dispatch(setOptionRequiresRebuild(false));
    refresh();
  };

  return (
    <div className="planner-options">
      <p className="text-muted mb-3">Configure planner display options</p>
      {requiresRebuild && (
        <div className="rebuild-alert">
          <Icon icon={faTriangleExclamation} size="2x" className="ms-2" />
          <span>Updated option(s) require planner rebuild for best results.</span>
          <Button onClick={closeAndRebuild} className="me-2">
            Rebuild
          </Button>
        </div>
      )}
      <CommonOptions options={options}></CommonOptions>
    </div>
  );
};
